// Janela "Customise Character" (o provador) no estilo do Tibia: prévia girando, partes do corpo + paleta de 133 cores,
// addons, montaria, e listas dos outfits e montarias que o personagem tem.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Outfit } from '../../../../shared/src/protocol';
import { MOUNT_LIST, OUTFIT_LIST } from '../../../../shared/src/game/outfit-list';
import { CUSTOM_OUTFITS, mountPoints, outfitPoints, ownedOutfits, type Cosmetics } from '../../../../shared/src/game/outfits';
import { STORE_MOUNTS, STORE_OUTFITS } from '../../../../shared/src/game/store-cosmetics';
import { TAMING } from '../../../../shared/src/game/taming';
import { outfitColor, renderMounted, renderOutfit } from '../outfit';
import { Portrait } from './BattleList';

type Part = 'head' | 'body' | 'legs' | 'feet';
const PARTS: Array<[Part, string]> = [['head', 'Head'], ['body', 'Primary'], ['legs', 'Secondary'], ['feet', 'Detail']];
const HELP_KEY = 'nx-help-outfits';
const seenHelp = () => { try { return localStorage.getItem(HELP_KEY) === '1'; } catch { return true; } };
const rgb = (i: number) => { const [r, g, b] = outfitColor(i); return `rgb(${r},${g},${b})`; };

/** Prévia animada (anda no lugar, como no Tibia) na direção escolhida. */
function Preview(props: { outfit: Outfit; dir: number; walk: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!props.walk) { setFrame(0); return; }
    const t = setInterval(() => setFrame((f) => (f + 1) % 8), 140);
    return () => clearInterval(t);
  }, [props.walk]);
  useEffect(() => {
    let ok = true;
    const o = props.outfit;
    const grp = props.walk ? 1 : 0;
    (o.mount ? renderMounted(o, o.mount, props.dir, grp, frame) : renderOutfit(o, props.dir, grp, frame)).then((c) => {
      const cv = ref.current; if (!ok || !cv) return;
      const g = cv.getContext('2d')!; g.clearRect(0, 0, cv.width, cv.height); g.imageSmoothingEnabled = false;
      // o pé do personagem fica no quadrado de baixo à direita do sprite: centraliza esse quadrado
      const sc = 2;
      g.drawImage(c, Math.round(cv.width / 2 - (c.width - 16) * sc), Math.round(cv.height - 14 - c.height * sc), c.width * sc, c.height * sc);
    }).catch(() => {});
    return () => { ok = false; };
  }, [JSON.stringify(props.outfit), props.dir, frame]);
  return <canvas ref={ref} width={170} height={160} class="cc-canvas" />;
}

export function Outfits(props: { outfit: Outfit; sex: 'male' | 'female'; cosmetics: Cosmetics; onSave: (o: Outfit, mount: number | null) => void; onClose?: () => void }) {
  const [o, setO] = useState<Outfit>({ ...props.outfit, mount: undefined });
  const [part, setPart] = useState<Part>('head');
  const [dir, setDir] = useState(2);
  const [walk, setWalk] = useState(true);
  const [tab, setTab] = useState<'outfits' | 'mounts' | 'how'>('outfits');
  const [q, setQ] = useState('');
  const [mount, setMount] = useState<number | null>(props.cosmetics.mount ?? null);
  const [lastMount, setLastMount] = useState<number | null>(props.cosmetics.mount ?? (props.cosmetics.mounts ?? [])[0] ?? null);
  const owned = ownedOutfits(props.cosmetics);
  const look = (d: { male: number; female: number }) => (props.sex === 'female' ? d.female : d.male);
  const cur = owned.find((x) => x.def.male === o.lookType || x.def.female === o.lookType);
  const mounts = MOUNT_LIST.filter((m) => (props.cosmetics.mounts ?? []).includes(m.id));
  const curMount = MOUNT_LIST.find((m) => m.id === mount);
  const shown: Outfit = { ...o, ...(curMount ? { mount: curMount.look } : {}) };
  const match = (n: string) => !q || n.toLowerCase().includes(q.toLowerCase());
  const [help, setHelp] = useState(() => !seenHelp());
  const toggleHelp = () => { setHelp(!help); try { localStorage.setItem(HELP_KEY, '1'); } catch { /* sem storage */ } };
  const totalOutfits = OUTFIT_LIST.length + CUSTOM_OUTFITS.length;
  const addonsOwned = owned.reduce((s, x) => s + (x.addons & 1 ? 1 : 0) + (x.addons & 2 ? 1 : 0), 0);
  const pctOut = Math.round((owned.length / Math.max(1, totalOutfits)) * 100);
  const pctMount = Math.round((mounts.length / Math.max(1, MOUNT_LIST.length)) * 100);

  // setas de outfit/montaria, como no Tibia antigo (anterior / próximo)
  const stepOutfit = (d: number) => {
    if (!owned.length) return;
    const i = Math.max(0, owned.findIndex((x) => x === cur));
    const n = owned[(i + d + owned.length) % owned.length]!;
    setO({ ...o, lookType: look(n.def), addons: o.addons & n.addons });
  };
  const stepMount = (d: number) => {
    if (!mounts.length) return;
    const i = Math.max(0, mounts.findIndex((m) => m.id === (mount ?? lastMount)));
    const n = mounts[(i + d + mounts.length) % mounts.length]!;
    setMount(n.id); setLastMount(n.id);
  };

  return (
    <div class="of">
      {/* cabeçalho: o provador */}
      <div class="of-head">
        <div class="of-hanger">👗</div>
        <div class="of-head-txt">
          <div class="of-title">Provador de Noxara <button class="of-help" onClick={toggleHelp} title="Como funciona">?</button></div>
          <div class="of-sub">Escolha a roupa, pinte cada parte e suba na montaria. O espelho mostra tudo antes de salvar.</div>
        </div>
        <div class="of-stats">
          <div class="of-stat" title="Outfits que você tem"><small>Outfits</small><b>{owned.length}<em>/{totalOutfits}</em></b><span class="of-bar"><i style={{ width: pctOut + '%' }} /></span></div>
          <div class="of-stat" title="Addons liberados nos seus outfits"><small>Addons</small><b>{addonsOwned}<em>/{owned.length * 2}</em></b></div>
          <div class="of-stat" title="Montarias que você tem"><small>Montarias</small><b>{mounts.length}<em>/{MOUNT_LIST.length}</em></b><span class="of-bar m"><i style={{ width: pctMount + '%' }} /></span></div>
          <div class="of-stat pts" title="Pontos no ranking de outfits e de montarias"><small>Ranking</small><b>{outfitPoints(props.cosmetics)}<em>+{mountPoints(props.cosmetics)}</em></b></div>
        </div>
      </div>

      {help && (
        <div class="of-steps">
          <div><i>🪞</i><b>1. Espelho</b><small>Gire com as setas e veja andando (Show Movement) ou montado (Show Mount).</small></div>
          <div><i>🎨</i><b>2. Cores</b><small>Escolha a parte (Head, Primary, Secondary, Detail) e clique numa das 133 cores.</small></div>
          <div><i>✨</i><b>3. Addons e montaria</b><small>Marque os addons que você já tem e escolha a montaria nas listas.</small></div>
          <div><i>💾</i><b>4. Salve</b><small>Clique em Ok para vestir. Em "Como conseguir" você vê onde achar o que falta.</small></div>
        </div>
      )}

      <div class="of-body">
      <div class="cc-left">
        <div class="cc-box cc-preview-box of-mirror">
          <div class="of-glass"><Preview outfit={shown} dir={dir} walk={walk} /></div>
          <div class="of-swatches" title="Cores atuais">{PARTS.map(([k, l]) => <i key={k} title={l} style={{ background: rgb(o[k]) }} />)}</div>
          <div class="cc-rot">
            <button class="tb-button" title="Girar para a esquerda" onClick={() => setDir((dir + 1) % 4)}>◄</button>
            <button class="tb-button" title="Girar para a direita" onClick={() => setDir((dir + 3) % 4)}>►</button>
          </div>
        </div>
        <div class="cc-box cc-checks">
          <label><input type="checkbox" checked={walk} onChange={() => setWalk(!walk)} /> Show Movement</label>
          <label class={mounts.length ? '' : 'muted'}><input type="checkbox" disabled={!mounts.length} checked={!!mount}
            onChange={() => setMount(mount ? null : lastMount ?? mounts[0]?.id ?? null)} /> Show Mount</label>
        </div>
        <div class="cc-box">
          <div class="cc-sel">
            <button class="tb-button" onClick={() => stepOutfit(-1)}>◄</button>
            <div class="cc-name"><small>Outfit</small><b>{cur?.def.name ?? '—'}</b></div>
            <button class="tb-button" onClick={() => stepOutfit(1)}>►</button>
          </div>
          <div class="cc-addons">
            {[1, 2].map((a) => (
              <label key={a} class={cur && cur.addons & a ? '' : 'muted'} title={cur && !(cur.addons & a) ? 'Você ainda não tem este addon' : ''}>
                <input type="checkbox" disabled={!cur || !(cur.addons & a)} checked={!!(o.addons & a)}
                  onChange={(e) => setO({ ...o, addons: (e.target as HTMLInputElement).checked ? o.addons | a : o.addons & ~a })} /> Addon {a}
              </label>
            ))}
          </div>
          <div class="cc-sel">
            <button class="tb-button" disabled={!mounts.length} onClick={() => stepMount(-1)}>◄</button>
            <div class="cc-name"><small>Mount</small><b>{curMount?.name ?? (mounts.length ? 'Sem montaria' : 'Nenhuma')}</b></div>
            <button class="tb-button" disabled={!mounts.length} onClick={() => stepMount(1)}>►</button>
          </div>
        </div>
      </div>

      <div class="cc-right">
        <div class="cc-box">
          <div class="cc-parts">{PARTS.map(([k, l]) => <button key={k} class={'tb-button' + (part === k ? ' green' : '')} onClick={() => setPart(k)}><i class="of-chip" style={{ background: rgb(o[k]) }} />{l}</button>)}</div>
          <div class="cc-palette">
            {Array.from({ length: 133 }, (_, i) => {
              const [r, g, b] = outfitColor(i);
              return <i key={i} class={o[part] === i ? 'on' : ''} style={{ background: `rgb(${r},${g},${b})` }} onClick={() => setO({ ...o, [part]: i })} />;
            })}
          </div>
        </div>
        <div class="cc-box cc-listbox">
          <div class="cc-tabs">
            <button class={'tb-button' + (tab === 'outfits' ? ' green' : '')} onClick={() => setTab('outfits')}>Outfits ({owned.length}/{totalOutfits})</button>
            <button class={'tb-button' + (tab === 'mounts' ? ' green' : '')} onClick={() => setTab('mounts')}>Mounts ({mounts.length}/{MOUNT_LIST.length})</button>
            <button class={'tb-button' + (tab === 'how' ? ' green' : '')} onClick={() => setTab('how')}>Como conseguir</button>
            <input class="cc-search" placeholder="Procurar..." value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          </div>
          <div class="cc-list">
            {tab === 'how' ? <HowToGet owned={owned.map((x) => x.def.name)} mounts={props.cosmetics.mounts ?? []} sex={props.sex} match={match} /> : tab === 'outfits' ? owned.filter(({ def }) => match(def.name)).map(({ def, addons }) => (
              <button key={def.name} class={'cc-tile' + (look(def) === o.lookType ? ' on' : '')} title={def.name}
                onClick={() => setO({ ...o, lookType: look(def), addons: o.addons & addons })}>
                <Portrait outfit={{ ...o, lookType: look(def), addons, mount: undefined }} size={40} />
                <small>{def.name}</small>
                <span class="cc-adds">{[1, 2].map((a) => <i key={a} class={addons & a ? 'on' : ''} />)}</span>
              </button>
            )) : <>
              <button class={'cc-tile' + (!mount ? ' on' : '')} onClick={() => setMount(null)}><span class="cc-none">✕</span><small>Sem montaria</small></button>
              {mounts.filter((m) => match(m.name)).map((m) => (
                <button key={m.id} class={'cc-tile' + (mount === m.id ? ' on' : '')} title={`${m.name} (+${m.speed} de velocidade)`} onClick={() => { setMount(m.id); setLastMount(m.id); }}>
                  <Portrait outfit={{ lookType: m.look, head: 0, body: 0, legs: 0, feet: 0, addons: 0 }} size={40} />
                  <small>{m.name}</small>
                </button>
              ))}
              {!mounts.length && <p class="muted small cc-hint">Você ainda não tem montarias. Compre na Noxara Store ou dome nas hunts usando o item certo perto do monstro, como no Tibia.</p>}
            </>}
          </div>
          <div class="cc-rank small muted">Ranking: outfits {outfitPoints(props.cosmetics)} pts · montarias {mountPoints(props.cosmetics)} pts</div>
        </div>
        <div class="cc-foot">
          <button class="tb-button green" onClick={() => props.onSave(o, mount)}>Ok</button>
          {props.onClose && <button class="tb-button" onClick={props.onClose}>Cancel</button>}
        </div>
      </div>
      </div>
    </div>
  );
}

/** Outfits e montarias que o personagem ainda não tem, e onde conseguir cada um. */
function HowToGet(props: { owned: string[]; mounts: number[]; sex: 'male' | 'female'; match: (n: string) => boolean }) {
  const store = new Map(STORE_OUTFITS.map((o) => [o.name.toLowerCase(), o]));
  const outfits = [...OUTFIT_LIST, ...CUSTOM_OUTFITS].filter((d) => !d.free && !props.owned.includes(d.name) && props.match(d.name));
  const smount = new Map(STORE_MOUNTS.map((m) => [m.id, m]));
  const mounts = MOUNT_LIST.filter((m) => !props.mounts.includes(m.id) && props.match(m.name));
  const where = (name: string) => {
    const c = CUSTOM_OUTFITS.find((x) => x.name === name);
    if (c) return c.desc;
    const st = store.get(name.toLowerCase());
    return st ? `Noxara Store, aba Outfits: ${st.price} coins (com os 2 addons).` : 'Ainda sem forma de conseguir (em breve).';
  };
  const mwhere = (id: number) => {
    const t = TAMING.find((x) => x.mount === id);
    if (t) return `Domar: use ${t.itemName} perto de um ${t.monster} (aparece raramente nas hunts do tema; o item cai do próprio monstro).`;
    const st = smount.get(id);
    return st ? `Noxara Store, aba Montarias: ${st.price} coins.` : 'Ainda sem forma de conseguir (em breve).';
  };
  return (
    <div class="cc-how">
      <h5>Outfits que faltam ({outfits.length})</h5>
      {outfits.map((d) => (
        <div key={d.name} class="cc-how-row">
          <Portrait outfit={{ lookType: props.sex === 'female' ? d.female : d.male, head: 78, body: 69, legs: 58, feet: 76, addons: 3 }} size={32} />
          <div><b>{d.name}</b><small>{where(d.name)}</small></div>
        </div>
      ))}
      <h5>Montarias que faltam ({mounts.length})</h5>
      {mounts.map((m) => (
        <div key={m.id} class="cc-how-row">
          <Portrait outfit={{ lookType: m.look, head: 0, body: 0, legs: 0, feet: 0, addons: 0 }} size={32} />
          <div><b>{m.name}</b><small>{mwhere(m.id)}</small></div>
        </div>
      ))}
    </div>
  );
}
